import { ChevronRight, ExternalLink } from 'lucide-react'

import { useGetBrokersMembersEnrollments } from 'components/hooks/needs_attention/useGetBrokersMembersEnrollments'
import { useGetBrokersEmployerICHRAConfigs } from 'components/hooks/useGetBrokersEmployerICHRAConfigs'
import { Banner } from 'components/reusable/Banner'
import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import Notes from 'components/reusable/MemberNotes'
import { adminEmployers, adminMembers, adminRecordInspector, brokersMembers } from 'rails_routes'
import { Badge } from 'slab'
import { type EmployerICHRAConfig } from 'types'
import { useGetBrokersMembersMemberId } from 'utils/web_api'

import BrokersMembersEnrollmentStatusOutput from './BrokersMembersEnrollmentStatusOutput'
import { BrokersMembersTransactionsTableImpl } from './BrokersMembersTransactionsTable'
import EmploymentStatusBadge from './EmploymentStatusBadge'
import NeedsAttentionMemberView from './needs_attention/NeedsAttentionMemberView'
import { type Broker } from './types'

type BrokersMembersTransactionsViewProps = {
  memberId: string
  currentBroker: Broker
  isUserThatchAdmin: boolean
  isNAVAdmin: boolean
  isTieredAllowancesEmployer: boolean
  isEditEnrollmentV2Enabled: boolean
  hasUnmappedEnrollmentPayments: boolean
}

const BrokersMembersTransactionsViewImpl = ({
  memberId,
  currentBroker,
  isUserThatchAdmin,
  isNAVAdmin,
  isTieredAllowancesEmployer,
  isEditEnrollmentV2Enabled,
  hasUnmappedEnrollmentPayments,
}: BrokersMembersTransactionsViewProps) => {
  const { data: member, isLoading, error } = useGetBrokersMembersMemberId(memberId, {
    query: {
      enabled: !!memberId,
    },
  })
  const { data: employerICHRAConfigs, error: employerICHRAConfigsError } = useGetBrokersEmployerICHRAConfigs({
    employerId: member?.employer_id || '',
  })

  const employerICHRAConfigsByPlanYear: Record<number, EmployerICHRAConfig> =
    employerICHRAConfigs?.reduce<Record<number, EmployerICHRAConfig>>(
      (acc, curr) => ({ [curr.plan_year]: curr, ...acc }),
      {}
    ) || {}

  const {
    data: enrollments,
    error: enrollmentsError,
    isLoading: enrollmentsLoading,
  } = useGetBrokersMembersEnrollments({ memberId })

  if (isLoading) {
    return <LoadingSpinnerCentered />
  }

  if (error || employerICHRAConfigsError || !member) {
    const errorMsg = (error as any)?.message as string

    return (
      <Banner
        type='critical'
        titleText='Something went wrong. Please try again.'
      >
        {!!errorMsg && <div>{errorMsg}</div>}
      </Banner>
    )
  }

  // Display the latest plan year with a different colored badge for the year, making it easier to identify the current set of enrollments.
  // The latest plan year isn't always the current year, but this is close enough for UI purposes.
  const latestPlanYear = Math.max(
    ...(enrollments?.map((e) => e.plan_year) || [])
  )

  return (
    <div className='space-y-8'>
      <div className='flex justify-start items-center text-2xl font-bold space-x-2'>
        <div>{member.name}</div>
        <ChevronRight size={28} />
        <div>Transactions</div>
      </div>
      <div className='flex justify-start items-center space-x-2 flex-wrap flex-0 space-y-1'>
        <Badge
          actionIcon={ExternalLink}
          actionLabel='View broker portal'
          onActionClick={() => window.open(brokersMembers.show({ id: memberId }))}
        >
          Broker Portal
        </Badge>
        <Badge
          actionIcon={ExternalLink}
          actionLabel='View member admin'
          onActionClick={() => window.open(adminMembers.show({ id: memberId }))}
        >
          Member Admin
        </Badge>
        <Badge
          actionIcon={ExternalLink}
          actionLabel='View Employer'
          onActionClick={() =>
            window.open(adminEmployers.show({ id: member.employer_id }))}
        >
          ER: {member.employer_name}
        </Badge>
        <Badge
          actionIcon={ExternalLink}
          actionLabel='View Employer'
          onActionClick={() =>
            window.open(adminRecordInspector.show({ id: member.brokerage_id }))}
        >
          BKG: {member.brokerage_name}
        </Badge>
        <EmploymentStatusBadge member={member} />
      </div>

      <div>
        <NeedsAttentionMemberView
          memberId={memberId}
          currentBroker={currentBroker}
          isNAVAdmin={isNAVAdmin}
        />
      </div>

      {hasUnmappedEnrollmentPayments && (
        <div>
          <div className='text-lg font-bold mb-4'>
            Unmapped premium payments
          </div>
          <BrokersMembersTransactionsTableImpl
            isUserThatchAdmin={isUserThatchAdmin}
            memberId={memberId}
            enrollmentId='missing'
            isMemberTransactionsViewV2Enabled
            latestPlanYear={latestPlanYear}
            declareLossesAtTransactionLevel={false}
          />
        </div>
      )}

      {enrollmentsError && (
        <Badge type='critical'>Error loading enrollments</Badge>
      )}

      {enrollmentsLoading
        ? (
          <LoadingSpinnerCentered />
          )
        : (
          <div className='space-y-8'>
            {enrollments?.map((enrollment) => {
              return (
                <div key={enrollment.id}>
                  <div className='flex justify-start items-center space-x-2 text-lg font-bold'>
                    <Badge
                      type={
                      enrollment.plan_year === latestPlanYear
                        ? 'info'
                        : 'neutral'
                    }
                    >
                      {enrollment.plan_year}
                    </Badge>
                    <div>{enrollment.title}</div>
                    <Badge
                      onActionClick={() =>
                        window.open(adminRecordInspector.show({ id: enrollment.id }))}
                      actionIcon={ExternalLink}
                      actionLabel='Inspect enrollment'
                    >
                      {enrollment.id.slice(-6)}
                    </Badge>
                  </div>
                  <BrokersMembersEnrollmentStatusOutput
                    memberId={memberId}
                    enrollment={enrollment}
                    isUserThatchAdmin={isUserThatchAdmin}
                    planYear={enrollment.plan_year}
                    employerIchraConfig={
                    employerICHRAConfigsByPlanYear[enrollment.plan_year]
                  }
                    isEditEnrollmentV2Enabled={isEditEnrollmentV2Enabled}
                    isTieredAllowancesEmployer={isTieredAllowancesEmployer}
                  />
                  <BrokersMembersTransactionsTableImpl
                    memberId={memberId}
                    enrollmentId={enrollment.id}
                    isUserThatchAdmin={isUserThatchAdmin}
                    isMemberTransactionsViewV2Enabled
                    latestPlanYear={latestPlanYear}
                    declareLossesAtTransactionLevel={enrollment.declare_losses_at_transaction_level}
                  />
                </div>
              )
            })}
          </div>
          )}

      <div>
        <div className='text-lg font-bold mb-4'>Non-premium transactions</div>
        <BrokersMembersTransactionsTableImpl
          isUserThatchAdmin={isUserThatchAdmin}
          memberId={memberId}
          enrollmentId='none'
          isMemberTransactionsViewV2Enabled
          latestPlanYear={latestPlanYear}
          declareLossesAtTransactionLevel // non-premium transactions always have losses handled at the transaction level
        />
      </div>

      <div>
        <div className='text-lg font-bold mb-4'>Member Notes</div>
        <Notes memberId={memberId} />
      </div>
    </div>
  )
}

const BrokersMembersTransactionsView = (
  props: BrokersMembersTransactionsViewProps
) => {
  return <BrokersMembersTransactionsViewImpl {...props} />
}

export default BrokersMembersTransactionsView
